import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import Url from "../../apiBaseUrl/APIConfig";
import type { LogOutResponseBody, LogOutResponse } from "./Model";
import { setupInterceptorsTo } from "../authenticationInterceptors/Interceptors";

type InitialState = {
  loading: boolean;
  response: LogOutResponse;
  successMsg: string;
  errorMsg: string;
};

const initialState: InitialState = {
  loading: false,
  response: {} as LogOutResponse,
  successMsg: "",
  errorMsg: "",
};

export const logOut = createAsyncThunk<LogOutResponse, LogOutResponseBody>(
  "logOut/logOut",
  async (payload: LogOutResponseBody, { rejectWithValue }) => {
    const headers = {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: `Bearer ${payload.token}`,
    };

    try {
      const response = await setupInterceptorsTo(axios).post(
        Url.baseUrl + "/profileapp/logout/",
        { refresh_token: payload.refresh_token },
        { headers }
      );

      // Clear localStorage on successful logout
      localStorage.clear();

      return response?.data?.message !== undefined
        ? response?.data?.message !== null
          ? response?.data !== ""
            ? response?.data
            : "You have been logout successfully"
          : "You have been logout successfully"
        : "You have been logout successfully";

    } catch (error: unknown) {
      // Clear localStorage regardless of error (especially for 401)
      localStorage.clear();

      // Type narrowing for error object
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: unknown }).response === "object"
      ) {
        const errResponse = (error as { response: any }).response;
        // Check if it's a 401 error (expected after logout)
        if (errResponse?.status === 401) {
          // 401 is expected - treat as successful logout
          return {
            message: "You have been logout successfully"
          } as LogOutResponse;
        }

        // For other errors, reject with error message
        const errorMessage = errResponse?.data !== undefined
          ? errResponse?.data[0]?.description !== undefined
            ? errResponse?.data[0]?.description
            : errResponse.data.message !== undefined
              ? errResponse.data.message
              : "You have been logout forcefully"
          : "You have been logout forcefully";

        return rejectWithValue(errorMessage);
      }

      // If error is not an axios error object
      return rejectWithValue("You have been logout forcefully");
    }
  }
);

const logOutSlice = createSlice({
  name: "logOut",
  initialState,
  reducers: {
    updateLogOutClearMsg: (state) => {
      state.successMsg = "";
      state.errorMsg = "";
    },
    updateLogOutClearRes: (state) => {
      state.successMsg = "";
      state.errorMsg = "";
      state.response = {} as LogOutResponse;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logOut.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      logOut.fulfilled,
      (state, action: PayloadAction<LogOutResponse>) => {
        state.loading = false;
        state.response = action.payload;
        state.successMsg = action.payload.message;
        state.errorMsg = "";
      }
    );
    builder.addCase(logOut.rejected, (state, action) => {
      state.loading = false;
      state.response = {} as LogOutResponse;
      state.successMsg = "";
      state.errorMsg = String(action.payload!);
    });
  },
});

export const { updateLogOutClearMsg, updateLogOutClearRes } = logOutSlice.actions;
export default logOutSlice.reducer;